import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  listenerWeeklyPlanForEditor,
  pickOwnListeningProfile,
} from '../../util/ownListeningProfile';
import { availabilityExceptionSaveMessage, oneOffDayOffsFromExceptions } from '../../util/changeOneDay';
import {
  createListingAvailabilityExceptions,
  deleteListingAvailabilityException,
  fetchListingAvailabilityExceptions,
  saveListingAvailabilityPlan,
} from '../../util/api';

import ListenerHoursEditor from '../ListenerHoursEditor/ListenerHoursEditor';

import css from './ListenerCalendar.module.css';

/**
 * Listener dashboard weekly hours. Listing availabilityPlan is the source of truth.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.ownProfile]
 * @param {Array} [props.ownListings]
 * @param {string} [props.className]
 * @param {boolean} [props.flush]
 * @param {Function} [props.onSaved]
 * @param {Function} [props.onSaveHours]
 * @param {boolean} [props.asPage]
 * @returns {JSX.Element}
 */
const ListenerCalendar = props => {
  const {
    ownProfile,
    ownListings = [],
    className,
    flush = false,
    onSaved,
    onSaveHours = saveListingAvailabilityPlan,
    onCreateExceptions = createListingAvailabilityExceptions,
    onFetchExceptions = fetchListingAvailabilityExceptions,
    onDeleteException = deleteListingAvailabilityException,
    asPage = false,
    simple = false,
    saveMessageId,
    savedMessageId,
    saveTone,
  } = props;
  const intl = useIntl();
  const listing = pickOwnListeningProfile(ownListings) || ownProfile;
  const listingId = listing?.id?.uuid || listing?.id || null;
  const plan = listenerWeeklyPlanForEditor(listing);
  const timezone = listing?.attributes?.availabilityPlan?.timezone || plan?.timezone || '';
  const planReady = Array.isArray(plan?.entries) && plan.entries.length > 0;
  const planKey = `${listingId || 'none'}-${planReady ? 'ready' : 'empty'}`;
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [exceptionInProgress, setExceptionInProgress] = useState(false);
  const [exceptionError, setExceptionError] = useState(null);
  const [dayOffs, setDayOffs] = useState([]);
  const [dayOffDeleteId, setDayOffDeleteId] = useState(null);

  const listingKey = typeof listingId === 'string' ? listingId : listingId?.uuid || '';

  const loadDayOffs = useCallback(() => {
    if (!listingKey || typeof onFetchExceptions !== 'function') {
      return Promise.resolve([]);
    }
    try {
      return Promise.resolve(onFetchExceptions({ listingId: listingKey }))
        .then(payload => {
          const rows = oneOffDayOffsFromExceptions(payload?.data?.exceptions || [], timezone);
          setDayOffs(rows);
          return rows;
        })
        .catch(() => []);
    } catch (e) {
      return Promise.resolve([]);
    }
  }, [listingKey, onFetchExceptions, timezone]);

  useEffect(() => {
    loadDayOffs();
  }, [loadDayOffs]);

  const onSave = payload => {
    const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
    if (!id || typeof onSaveHours !== 'function') {
      return;
    }
    const weekly = payload?.publicData?.talkerAvailability || payload || {};
    setInProgress(true);
    setError(null);
    setSaved(false);
    Promise.resolve(
      onSaveHours({
        listingId: id,
        timezone: timezone || weekly.timezone,
        entries: weekly.entries,
      })
    )
      .then(() => {
        setInProgress(false);
        setSaved(true);
        if (typeof onSaved === 'function') {
          onSaved();
        }
      })
      .catch(e => {
        setInProgress(false);
        setSaved(false);
        setError(
          e?.error || e?.message || intl.formatMessage({ id: 'ListenerCalendar.saveError' })
        );
      });
  };

  const onSaveException = exceptions => {
    const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
    if (!id) {
      return Promise.reject(new Error('missing listing'));
    }
    setExceptionInProgress(true);
    setExceptionError(null);
    return Promise.resolve(
      onCreateExceptions({
        listingId: id,
        exceptions,
      })
    )
      .then(payload => {
        setExceptionInProgress(false);
        return loadDayOffs().then(() => {
          if (typeof onSaved === 'function') {
            onSaved();
          }
          return payload;
        });
      })
      .catch(e => {
        const message =
          availabilityExceptionSaveMessage(e) ||
          intl.formatMessage({ id: 'ListenerCalendar.saveError' });
        setExceptionInProgress(false);
        setExceptionError(message);
        throw e;
      });
  };

  return (
    <section
      className={classNames(css.root, { [css.flush]: flush }, className)}
      aria-labelledby="listener-calendar-heading"
      data-testid="listener-calendar"
      data-listing-id={listingId || ''}
      data-plan-entries={Array.isArray(plan?.entries) ? plan.entries.length : 0}
      data-available-now={listing?.attributes?.publicData?.availableNow === true ? 'true' : 'false'}
      data-has-saved-plan={
        listing?.attributes?.publicData?.slPlanBeforeAvailableNow?.entries?.length
          ? 'true'
          : 'false'
      }
    >
      {listingId ? (
        <ListenerHoursEditor
          key={planKey}
          initialPlan={plan}
          timezone={timezone}
          inProgress={inProgress}
          error={error}
          saved={saved}
          onSave={onSave}
          headingTag={asPage ? 'h1' : 'h2'}
          saveMessageId={saveMessageId || 'ListenerCalendar.saveSchedule'}
          savedMessageId={savedMessageId}
          saveTone={saveTone || 'pink'}
          simple={simple}
          onSaveException={simple ? undefined : onSaveException}
          exceptionInProgress={simple ? false : exceptionInProgress}
          exceptionError={simple ? null : exceptionError}
          dayOffs={simple ? [] : dayOffs}
          dayOffDeleteId={simple ? null : dayOffDeleteId}
          onDeleteDayOff={simple ? undefined : (exceptionId => {
            if (!listingKey || typeof onDeleteException !== 'function') {
              return Promise.resolve();
            }
            setDayOffDeleteId(exceptionId);
            setExceptionError(null);
            return Promise.resolve(onDeleteException({ listingId: listingKey, exceptionId }))
              .then(() => loadDayOffs())
              .catch(e => {
                setExceptionError(
                  e?.error || e?.message || intl.formatMessage({ id: 'ListenerCalendar.saveError' })
                );
              })
              .finally(() => setDayOffDeleteId(null));
          })}
        />
      ) : (
        <>
          <h2 id="listener-calendar-heading" className={css.title}>
            <FormattedMessage id="ListenerCalendar.title" />
          </h2>
          <p className={css.lede}>
            <FormattedMessage id="ListenerCalendar.lede" />
          </p>
        </>
      )}
    </section>
  );
};

export default ListenerCalendar;
